"use client";

import { useContext, useEffect } from 'react';
import { AppContext } from '@/context/AppContext';
import { motion, Variants } from 'framer-motion';
import { ContactHeader } from "@/components/contact/ContactHeader";
import { ContactInfo } from "@/components/contact/ContactInfo";
import { ContactForm } from "@/components/contact/ContactForm";
import { ContactData } from "@/data/types";
import { useData } from "@/i18n/LangContext";

// Verwende den vorhandenen ContactData-Typ für saubere Typisierung
export function ContactPageClient({
                                    title,
                                    intro,
                                    formLabels,
                                    formPlaceholders,
                                    contactInfo,
                                    socialLinks,
                                    successMessage,
                                    errorMessage
                                  }: ContactData) {
  const { ui } = useData();
  const { setActivePage } = useContext(AppContext);

  useEffect(() => {
    setActivePage('contact');
  }, [setActivePage]);

  // Animation Varianten - mit Home-Seite abgestimmt
  const pageVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants: Variants = {
    initial: { y: 20, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', damping: 15 }
    }
  };

  return (
    <motion.div
      className="w-full min-h-full flex flex-col items-center justify-center py-4 md:py-16 transition-all duration-400"
      initial="initial"
      animate="animate"
      variants={pageVariants}
    >
      <div className="max-w-4xl w-full">
        <ContactHeader
          title={title}
          intro={intro}
          itemVariants={itemVariants}
        />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Kontakt-Info Box */}
          <motion.div
            className="md:col-span-4 order-2 md:order-1"
            variants={itemVariants}
          >
            <ContactInfo
              contactInfo={contactInfo}
              socialLinks={socialLinks}
            />
          </motion.div>

          {/* Formular Box */}
          <motion.div
            className="md:col-span-8 order-1 md:order-2"
            variants={itemVariants}
          >
            <ContactForm
              formLabels={formLabels}
              formPlaceholders={formPlaceholders}
              successMessage={successMessage}
              errorMessage={errorMessage}
            />
          </motion.div>
        </div>

        {/* Unterer Bereich */}
        <motion.div
          className="mt-16 text-center"
          variants={itemVariants}
        >
          <motion.div
            className="inline-block mx-auto bg-[rgba(90,21,16,0.2)] backdrop-blur-sm border border-[#ff8855]/20 rounded-xl px-6 py-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, type: 'spring', damping: 15 }}
            whileHover={{ y: -5 }}
          >
            <h3 className="text-lg font-medium mb-1 text-[#ff8855]">
              {contactInfo.thankYou?.title || ui.contact.thankYouTitle}
            </h3>
            <p className="text-white/70">
              {contactInfo.thankYou?.text || ui.contact.thankYouText}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
}